"use client";

import {createCheckbox} from "@gluestack-ui/checkbox";
import type {IPrimitiveIcon} from "@gluestack-ui/icon";
import {createIcon, PrimitiveIcon, Svg, UIIcon} from "@gluestack-ui/icon";
import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {useStyleContext, withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import {cssInterop} from "nativewind";
import React from "react";
import {Pressable, Text, View} from "react-native";
import type {TextProps, ViewProps} from "react-native";
import {Path} from "react-native-svg";

import {
    checkboxIconStyle,
    checkboxIndicatorStyle,
    checkboxLabelStyle,
    checkboxStyle
} from "./styles";

const CheckIcon = createIcon({
    Root: Svg,
    viewBox: "0 0 24 24",
    path: (
        <>
            <Path
                d="M20 6L9 17L4 12"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
            />
        </>
    )
});

const RemoveIcon = createIcon({
    Root: Svg,
    viewBox: "0 0 24 24",
    path: (
        <>
            <Path d="M5 12H19" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </>
    )
});

RemoveIcon.displayName = "RemoveIcon";

const IndicatorWrapper = React.forwardRef<React.ComponentRef<typeof View>, ViewProps>(
    function IndicatorWrapper({...props}, ref) {
        return <View {...props} ref={ref} />;
    }
);

const LabelWrapper = React.forwardRef<React.ComponentRef<typeof Text>, TextProps>(
    function LabelWrapper({...props}, ref) {
        return <Text {...props} ref={ref} />;
    }
);

const IconWrapper = React.forwardRef<React.ComponentRef<typeof PrimitiveIcon>, IPrimitiveIcon>(
    function IconWrapper({...props}, ref) {
        return <UIIcon {...props} ref={ref} />;
    }
);

const SCOPE = "CHECKBOX";
const UICheckbox = createCheckbox({
    Root: withStyleContext(Pressable, SCOPE),
    Group: View,
    Icon: IconWrapper,
    Label: LabelWrapper,
    Indicator: IndicatorWrapper
});

cssInterop(PrimitiveIcon, {
    className: {
        target: "style",
        nativeStyleToProp: {
            height: true,
            width: true,
            fill: true,
            color: "classNameColor",
            stroke: true
        }
    }
});

const CheckboxGroup = UICheckbox.Group;

type ICheckboxProps = React.ComponentPropsWithoutRef<typeof UICheckbox> &
    VariantProps<typeof checkboxStyle>;

const Checkbox = React.forwardRef<React.ComponentRef<typeof UICheckbox>, ICheckboxProps>(
    function Checkbox({className, size = "sm", id, isChecked, isIndeterminate, ...props}, ref) {
        return (
            <UICheckbox
                className={checkboxStyle({
                    class: className,
                    size
                })}
                id={id}
                isChecked={isChecked}
                testID={isChecked ? `${id}-checked` : `${id}-unchecked`}
                {...props}
                ref={ref}
                context={{
                    size,
                    isIndeterminate
                }}
                isIndeterminate={isIndeterminate}
            />
        );
    }
);

type ICheckboxIndicatorProps = React.ComponentPropsWithoutRef<typeof UICheckbox.Indicator> &
    VariantProps<typeof checkboxIndicatorStyle>;

const CheckboxIndicator = React.forwardRef<
    React.ComponentRef<typeof UICheckbox.Indicator>,
    ICheckboxIndicatorProps
>(function CheckboxIndicator({className, ...props}, ref) {
    const {size: parentSize} = useStyleContext(SCOPE);

    return (
        <UICheckbox.Indicator
            className={checkboxIndicatorStyle({
                parentVariants: {
                    size: parentSize
                },
                class: className
            })}
            {...props}
            ref={ref}
        />
    );
});

type ICheckboxLabelProps = React.ComponentPropsWithoutRef<typeof UICheckbox.Label> &
    VariantProps<typeof checkboxLabelStyle>;
const CheckboxLabel = React.forwardRef<
    React.ComponentRef<typeof UICheckbox.Label>,
    ICheckboxLabelProps
>(function CheckboxLabel({className, ...props}, ref) {
    const {size: parentSize} = useStyleContext(SCOPE);
    return (
        <UICheckbox.Label
            className={checkboxLabelStyle({
                parentVariants: {
                    size: parentSize
                },
                class: className
            })}
            {...props}
            ref={ref}
        />
    );
});

type ICheckboxIconProps = React.ComponentPropsWithoutRef<typeof UICheckbox.Icon> &
    VariantProps<typeof checkboxIconStyle>;

const CheckboxIcon = React.forwardRef<
    React.ComponentRef<typeof UICheckbox.Icon>,
    ICheckboxIconProps
>(function CheckboxIcon({className, size, as: asProp = CheckIcon, ...props}, ref) {
    const {size: parentSize, isIndeterminate} = useStyleContext(SCOPE);
    const as: React.ElementType<any> = isIndeterminate ? RemoveIcon : asProp;

    if (typeof size === "number") {
        return (
            <UICheckbox.Icon
                ref={ref}
                {...props}
                as={as}
                className={checkboxIconStyle({class: className})}
                forceMount={isIndeterminate}
                size={size}
            />
        );
    }
    if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
        return (
            <UICheckbox.Icon
                ref={ref}
                {...props}
                as={as}
                className={checkboxIconStyle({class: className})}
                forceMount={isIndeterminate}
            />
        );
    }

    return (
        <UICheckbox.Icon
            className={checkboxIconStyle({
                parentVariants: {
                    size: parentSize
                },
                class: className,
                size
            })}
            {...props}
            ref={ref}
            as={as}
            forceMount={isIndeterminate}
        />
    );
});

Checkbox.displayName = "Checkbox";
CheckboxIndicator.displayName = "CheckboxIndicator";
CheckboxLabel.displayName = "CheckboxLabel";
CheckboxIcon.displayName = "CheckboxIcon";

export {Checkbox, CheckboxIndicator, CheckboxLabel, CheckboxIcon, CheckboxGroup};
